/**
 * Fade charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_beauty_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fadeChartsPack: ToilePack = {
    slug: 'fade-charts',
    category: 'beauty',
    name: 'Fade charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'heights', label: 'Fade heights', subject: 'a row of four side-view heads labelled "Low fade", "Mid fade", "High fade" and "Skin fade", the fade line at a different height on each'},
        {key: 'guards', label: 'Guard numbers', subject: 'one side-view head with the fade graded in bands labelled "#0", "#1", "#2", "#3" and "#4" from the neck up'},
        {key: 'taper', label: 'Taper and drop', subject: 'three side-view heads labelled "Taper", "Drop fade" and "Burst fade", the shaded area drawn behind the ear'},
    ],
    styles: [
        {key: 'poster', label: 'Barber poster', style: 'barbershop poster line art with bold black outlines and the fade shaded in dense black stipple that thins upward', palette: 'black ink only', framing: 'the heads evenly spaced with a white margin'},
        {key: 'pencil', label: 'Pencil sketch', style: 'graphite pencil sketch with fine hair strokes and soft smudged shading through the fade', palette: 'gray pencil only', framing: 'drawn on a plain white sketchbook page with a margin'},
        {key: 'tones', label: 'Gray bands', style: 'clean vector line art with the fade filled as stepped flat gray bands', palette: 'black outlines with three flat gray tones', framing: 'a tidy chart with thin divider lines between heads'},
    ],
    directions: ['curly top', 'with beard', 'kids fade'],
    directionHint: 'try: curly top',
    seo: {
        title: 'Fade Chart: Fade Heights, Guard Numbers, Taper',
        metaDescription: "Fade charts: side views of men's heads with each fade height, clipper guard numbers and the taper, in three styles.",
        h1: 'Fade chart: fade heights, guard numbers, taper and drop',
        tagline: 'Three readings, each drawn as a barber poster, a pencil sketch or gray bands.',
        intro: "A fade chart lets a barber and a client agree on how high a fade starts. The nine pictures show side views of men's heads with each fade height, one head graded in guard numbers from #0 to #4, and the taper, drop and burst shapes. Type a different subject or twist, such as curly top, and the page asks for it in the same three styles.",
        useCases: [
            'agreeing a fade height before the cut',
            'a reference for choosing a clipper guard',
            'a barber shop wall chart',
            "a visual for a barber apprentice's notes",
        ],
        faq: [
            {q: 'Can I print them?', a: 'Yes. They are drawn charts with labels, not photographs, so they print as reference sheets.'},
            {q: 'What do the guard numbers show?', a: 'One head is graded in bands from the neck up, labelled #0, #1, #2, #3 and #4, so each band can be matched to a guard number.'},
            {q: 'What is the difference between the styles?', a: 'Barber poster is bold black outlines with a fade in dense stipple that thins upward. Pencil sketch is graphite with fine hair strokes and smudged shading. Gray bands fills the fade in stepped flat gray.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type a direction, such as burst fade, and the page asks for that subject in the same three styles. It is a request, not a guaranteed result.'},
        ],
    },
}
